<template>
  <ul ref="mainmenu" @click="onMenuClick" class="mainmenu">
    <li @mouseover="menu = 'eavesdrop'" class="mainmenu-item">
      <b>Eavesdrop</b>
      <div v-if="menuOpened && menu === 'eavesdrop'" class="submenu-wrapper">
        <sub-menu>
          <div class="user-info">
            <div
              class="user-avatar"
              :style="{ backgroundImage: `url(${user.avatar_url})` }"
            ></div>
            <div>{{ user.first_name }} {{ user.last_name }}</div>
          </div>
          <hr />
          <sub-menu-item>
            <a @click.prevent.stop="logout" href="#">Logout...</a>
          </sub-menu-item>
        </sub-menu>
      </div>
    </li>
    <li
      v-if="$route.meta.section === 'mix'"
      @mouseover="menu = 'mixes'"
      class="mainmenu-item"
    >
      Mix
      <div v-if="menuOpened && menu === 'mixes'" class="submenu-wrapper">
        <sub-menu>
          <sub-menu-item>
            <a
              href="#"
              :disabled="!mixPropsDirty"
              @click.prevent.stop="saveMix"
            >
              Save
            </a>
          </sub-menu-item>
          <sub-menu-item>
            <a href="#" @click.prevent.stop="createMix"> New mix </a>
          </sub-menu-item>
          <sub-menu-item>
            <a href="#" @click.prevent.stop="cloneConfirm = true"> Clone... </a>
          </sub-menu-item>
          <sub-menu-item>
            <a href="#" @click.prevent.stop="deleteConfirm = true">
              Delete...
            </a>
          </sub-menu-item>
          <hr />
          <sub-menu-item v-if="stemsLink !== ''">
            <a :href="stemsLink" target="_blank"> Download Stems </a>
          </sub-menu-item>
          <sub-menu-item>
            <a href="#" @click.prevent.stop="openBounceForm"> Bounce Mix </a>
          </sub-menu-item>
        </sub-menu>
      </div>
    </li>
    <li @mouseover="menu = 'projects'" class="mainmenu-item">
      Project
      <div v-if="menuOpened && menu === 'projects'" class="submenu-wrapper">
        <sub-menu>
          <sub-menu-item>
            <a @click.prevent.stop="openNewProjectForm" href="#">
              Create new...
            </a>
          </sub-menu-item>
          <sub-menu-item>
            <router-link @click="menuOpened = false" to="/projects">
              Project Manager
            </router-link>
          </sub-menu-item>
          <hr />
          <sub-menu-item v-for="project in projectsSlice" :key="project._id">
            <router-link
              @click="menuOpened = false"
              :to="`/projects/${project._id}`"
            >
              {{ project.name }}
            </router-link>
          </sub-menu-item>
        </sub-menu>
      </div>
    </li>
    <li @mouseover="menu = 'bands'" class="mainmenu-item">
      Band
      <div v-if="menuOpened && menu === 'bands'" class="submenu-wrapper">
        <sub-menu>
          <sub-menu-item>
            <a href="#" @click.prevent.stop="openNewBandForm">
              Create new...
            </a>
          </sub-menu-item>
          <hr />
          <sub-menu-item v-for="band in bands" :key="band._id">
            <router-link
              @click="menuOpened = false"
              :to="`/bands/${band._id}`"
              >{{ band.name }}</router-link
            >
          </sub-menu-item>
        </sub-menu>
      </div>
    </li>
  </ul>
  <confirm
    title="Clone Mix"
    v-if="cloneConfirm"
    @ok="cloneMix"
    @open="menuOpened = false"
    @click="cloneConfirm = false"
  />
  <confirm
    title="Delete Mix"
    v-if="deleteConfirm"
    @ok="deleteMix"
    @open="menuOpened = false"
    @click="deleteConfirm = false"
  />
  <modal v-if="newProjectForm">
    <div class="new-project-form modal-form">
      <div class="modal-form-header">Create New Project</div>
      <div class="modal-form-main">
        <div class="section-sub">
          <label>Name</label>
          <input
            ref="projectNameInput"
            class="input"
            type="text"
            v-model="projectName"
            placeholder="Project Name"
          />
        </div>
        <div class="section-sub">
          <label>Band</label>
          <bands-autocomplete @pick="projectBand = $event" />
        </div>
      </div>
      <div class="modal-form-buttons">
        <button @click.prevent.stop="createProject" class="btn-outline">
          Create
        </button>
        <button
          @click.prevent.stop="newProjectForm = false"
          class="btn-outline"
        >
          Cancel
        </button>
      </div>
    </div>
  </modal>
  <modal v-if="newBandForm">
    <div class="new-band-form modal-form">
      <div class="modal-form-header">Create New Band</div>
      <div class="modal-form-main">
        <div class="section-sub">
          <label>Name</label>
          <input
            ref="bandNameInput"
            class="input"
            type="text"
            v-model="bandName"
            placeholder="Band Name"
          />
        </div>
      </div>
      <div class="modal-form-buttons">
        <button @click.prevent.stop="createBand" class="btn-outline">
          Create
        </button>
        <button @click.prevent.stop="newBandForm = false" class="btn-outline">
          Cancel
        </button>
      </div>
    </div>
  </modal>
  <bounce-modal v-if="bounceForm" @close="bounceForm = false" />
</template>

<script lang="ts">
import { Band, Mix, Project, User } from "@/models";
import { Options, Vue } from "vue-class-component";
import { mapActions, mapState } from "vuex";
import SubMenu from "./SubMenu.vue";
import SubMenuItem from "./SubMenuItem.vue";
import Confirm from "./Confirm.vue";
import Modal from "./Modal.vue";
import BandsAutocomplete from "./Autocomplete/BandsAutocomplete.vue";
import api from "@/api";
import BounceModal from "./BounceModal.vue";

@Options({
  components: {
    Confirm,
    Modal,
    SubMenu,
    SubMenuItem,
    BandsAutocomplete,
    BounceModal,
  },
  data() {
    return {
      menu: "eavesdrop",
      menuOpened: false,
      cloneConfirm: false,
      deleteConfirm: false,
      newProjectForm: false,
      projectBand: null,
      projectName: "",
      newBandForm: false,
      bounceForm: false,
      bandName: "",
    };
  },
  computed: {
    ...mapState("bands", ["bands"]),
    ...mapState("projects", { project: "current", projects: "projects" }),
    ...mapState("mixes", ["mixPropsDirty", "mix"]),
    ...mapState(["user"]),
  },
  methods: {
    ...mapActions(["logout"]),
  },
})
export default class MainMenu extends Vue {
  menu!: string;
  menuOpened!: boolean;
  projects!: Project[];
  project!: Project;
  bands!: Band[];
  mixPropsDirty!: boolean;

  cloneConfirm!: boolean;
  deleteConfirm!: boolean;
  newProjectForm!: boolean;
  newBandForm!: boolean;
  bounceForm!: boolean;

  projectName!: string;
  projectBand!: Band | null;

  bandName!: string;
  user!: User;
  mix!: Mix | null;

  logout!: () => Promise<void>;

  saveMix() {
    this.$store.dispatch("mixes/saveMix");
    this.menuOpened = false;
  }

  get stemsLink() {
    if (!this.mix) return "";
    return `/api/v1/mixes/${this.mix._id}/download`;
  }

  get projectsSlice() {
    return this.projects.slice(0, 10);
  }

  mounted() {
    window.addEventListener("click", this.onClickOutside);
  }

  beforeUnmount() {
    window.removeEventListener("click", this.onClickOutside);
  }

  onMenuClick(e: MouseEvent) {
    let node = e.target as HTMLElement | null;
    while (node) {
      if (node.classList?.contains("mainmenu-item")) {
        this.menuOpened = true;
        return;
      }
      node = node.parentNode as HTMLElement | null;
    }
    this.menuOpened = false;
  }

  onClickOutside(e: MouseEvent) {
    let node = e.target as HTMLElement | null;
    while (node) {
      if (node === this.$refs.mainmenu) {
        return;
      }
      node = node.parentNode as HTMLElement | null;
    }
    this.menuOpened = false;
  }

  async cloneMix() {
    const mix: Mix = await this.$store.dispatch("mixes/cloneMix");
    this.$router.push(`/mixes/${mix._id}`);
  }

  deleteMix() {
    this.$store.dispatch("mixes/deleteMix").then(() => {
      this.$router.push(`/projects/${this.project._id}`);
    });
  }

  openBounceForm() {
    this.bounceForm = true;
    this.menuOpened = false;
  }

  createMix() {
    this.$store.dispatch("projects/createMix").then((mix) => {
      this.$router.push(`/mixes/${mix._id}`);
    });
  }

  openNewProjectForm() {
    this.newProjectForm = true;
    this.menuOpened = false;
    this.$nextTick(() => {
      const input = this.$refs.projectNameInput as HTMLElement;
      input.focus();
    });
  }

  openNewBandForm() {
    this.newBandForm = true;
    this.menuOpened = false;
    this.$nextTick(() => {
      const input = this.$refs.bandNameInput as HTMLElement;
      input.focus();
    });
  }

  async createProject() {
    if (this.projectBand === null) {
      this.$store.dispatch("messages/error", "You must pick a band");
      return;
    }
    const project = await api.projects.create({
      name: this.projectName,
      band_id: this.projectBand._id,
    });
    this.newProjectForm = false;
    this.$router.push(`/projects/${project._id}`);
    this.$store.dispatch("projects/loadProjectList");
  }

  async createBand() {
    const band = await api.bands.create({
      name: this.bandName,
    });
    this.newBandForm = false;
    this.$router.push(`/bands/${band._id}`);
    this.$store.dispatch("bands/loadBandList");
  }
}
</script>

<style lang="scss">
ul.mainmenu {
  background: #e0e0e0;
  background: linear-gradient(180deg, #f0f0f0 0%, #e0e0e0 100%);
  color: black;
  padding: 4px 8px;
  margin: 0;
  list-style: none;
  user-select: none;

  li.mainmenu-item {
    display: inline-block;
    cursor: pointer;
    margin-right: 1em;
    position: relative;
    .submenu-wrapper {
      position: absolute;
      top: 20px;
      z-index: 100;
    }
  }
}

.user-info {
  padding: 4px 8px;
  display: flex;
  align-items: center;
  .user-avatar {
    width: 20px;
    height: 20px;
    background-size: contain;
    border-radius: 50px;
    margin-right: 4px;
  }
}
</style>
